/**
 * Avatar utilities for preloaded avatar system
 * Provides COPPA-compliant avatar management without user uploads
 */

export interface AvatarData {
  id: string;
  name: string;
  emoji: string;
  color: string;
  category: 'animals' | 'faces' | 'objects' | 'nature' | 'symbols';
}

// Predefined avatar options - COPPA compliant, no user uploads
export const AVATAR_OPTIONS: AvatarData[] = [
  // Musical items moved to Objects category (unique emojis only)
  { id: 'piano', name: 'Piano', emoji: '🎹', color: '#8B4513', category: 'objects' },
  { id: 'guitar', name: 'Guitar', emoji: '🎸', color: '#D2691E', category: 'objects' },
  { id: 'violin', name: 'Violin', emoji: '🎻', color: '#DEB887', category: 'objects' },
  { id: 'saxophone', name: 'Saxophone', emoji: '🎷', color: '#C0C0C0', category: 'objects' },
  { id: 'trumpet', name: 'Trumpet', emoji: '🎺', color: '#FFD700', category: 'objects' },
  { id: 'drum', name: 'Drum', emoji: '🥁', color: '#8B0000', category: 'objects' },
  { id: 'microphone', name: 'Microphone', emoji: '🎤', color: '#000000', category: 'objects' },
  { id: 'headphones', name: 'Headphones', emoji: '🎧', color: '#800080', category: 'objects' },
  { id: 'musical-note', name: 'Musical Note', emoji: '🎶', color: '#FF69B4', category: 'objects' },
  { id: 'music-notes', name: 'Music Notes', emoji: '🎵', color: '#FF69B4', category: 'objects' },

  // Animals - Expanded collection for popular category
  { id: 'cat', name: 'Cat', emoji: '🐱', color: '#FFA500', category: 'animals' },
  { id: 'dog', name: 'Dog', emoji: '🐶', color: '#8B4513', category: 'animals' },
  { id: 'rabbit', name: 'Rabbit', emoji: '🐰', color: '#FFFFFF', category: 'animals' },
  { id: 'frog', name: 'Frog', emoji: '🐸', color: '#32CD32', category: 'animals' },
  { id: 'koala', name: 'Koala', emoji: '🐨', color: '#808080', category: 'animals' },
  { id: 'panda', name: 'Panda', emoji: '🐼', color: '#000000', category: 'animals' },
  { id: 'fox', name: 'Fox', emoji: '🦊', color: '#FF4500', category: 'animals' },
  { id: 'tiger', name: 'Tiger', emoji: '🐯', color: '#FF8C00', category: 'animals' },
  { id: 'lion', name: 'Lion', emoji: '🦁', color: '#DAA520', category: 'animals' },
  { id: 'elephant', name: 'Elephant', emoji: '🐘', color: '#696969', category: 'animals' },
  { id: 'bear', name: 'Bear', emoji: '🐻', color: '#8B4513', category: 'animals' },
  { id: 'penguin', name: 'Penguin', emoji: '🐧', color: '#000000', category: 'animals' },
  { id: 'owl', name: 'Owl', emoji: '🦉', color: '#8B4513', category: 'animals' },
  { id: 'duck', name: 'Duck', emoji: '🦆', color: '#FFD700', category: 'animals' },
  { id: 'chicken', name: 'Chicken', emoji: '🐔', color: '#FFD700', category: 'animals' },
  { id: 'pig', name: 'Pig', emoji: '🐷', color: '#FFB6C1', category: 'animals' },
  { id: 'cow', name: 'Cow', emoji: '🐮', color: '#FFFFFF', category: 'animals' },
  { id: 'horse', name: 'Horse', emoji: '🐴', color: '#8B4513', category: 'animals' },
  { id: 'monkey', name: 'Monkey', emoji: '🐵', color: '#8B4513', category: 'animals' },
  { id: 'gorilla', name: 'Gorilla', emoji: '🦍', color: '#696969', category: 'animals' },
  { id: 'zebra', name: 'Zebra', emoji: '🦓', color: '#000000', category: 'animals' },
  { id: 'giraffe', name: 'Giraffe', emoji: '🦒', color: '#FFD700', category: 'animals' },
  { id: 'hippo', name: 'Hippo', emoji: '🦛', color: '#696969', category: 'animals' },
  { id: 'rhino', name: 'Rhino', emoji: '🦏', color: '#808080', category: 'animals' },
  { id: 'camel', name: 'Camel', emoji: '🐪', color: '#D2691E', category: 'animals' },
  { id: 'llama', name: 'Llama', emoji: '🦙', color: '#FFFFFF', category: 'animals' },
  { id: 'deer', name: 'Deer', emoji: '🦌', color: '#8B4513', category: 'animals' },
  { id: 'mouse', name: 'Mouse', emoji: '🐭', color: '#808080', category: 'animals' },
  { id: 'hamster', name: 'Hamster', emoji: '🐹', color: '#FFD700', category: 'animals' },
  { id: 'squirrel', name: 'Squirrel', emoji: '🐿️', color: '#8B4513', category: 'animals' },
  { id: 'hedgehog', name: 'Hedgehog', emoji: '🦔', color: '#8B4513', category: 'animals' },
  { id: 'bat', name: 'Bat', emoji: '🦇', color: '#000000', category: 'animals' },
  { id: 'sloth', name: 'Sloth', emoji: '🦥', color: '#8B4513', category: 'animals' },
  { id: 'otter', name: 'Otter', emoji: '🦦', color: '#87CEEB', category: 'animals' },
  { id: 'skunk', name: 'Skunk', emoji: '🦨', color: '#000000', category: 'animals' },
  { id: 'kangaroo', name: 'Kangaroo', emoji: '🦘', color: '#FFD700', category: 'animals' },
  { id: 'raccoon', name: 'Raccoon', emoji: '🦝', color: '#808080', category: 'animals' },
  { id: 'badger', name: 'Badger', emoji: '🦡', color: '#8B4513', category: 'animals' },
  { id: 'beaver', name: 'Beaver', emoji: '🦫', color: '#8B4513', category: 'animals' },
  { id: 'seal', name: 'Seal', emoji: '🦭', color: '#808080', category: 'animals' },
  { id: 'whale', name: 'Whale', emoji: '🐋', color: '#87CEEB', category: 'animals' },
  { id: 'dolphin', name: 'Dolphin', emoji: '🐬', color: '#87CEEB', category: 'animals' },
  { id: 'shark', name: 'Shark', emoji: '🦈', color: '#C0C0C0', category: 'animals' },
  { id: 'octopus', name: 'Octopus', emoji: '🐙', color: '#FF69B4', category: 'animals' },
  { id: 'squid', name: 'Squid', emoji: '🦑', color: '#FF69B4', category: 'animals' },
  { id: 'lobster', name: 'Lobster', emoji: '🦞', color: '#FF4500', category: 'animals' },
  { id: 'crab', name: 'Crab', emoji: '🦀', color: '#FF4500', category: 'animals' },
  { id: 'shrimp', name: 'Shrimp', emoji: '🦐', color: '#FF69B4', category: 'animals' },
  { id: 'snail', name: 'Snail', emoji: '🐌', color: '#32CD32', category: 'animals' },
  { id: 'butterfly', name: 'Butterfly', emoji: '🦋', color: '#FF69B4', category: 'animals' },
  { id: 'bee', name: 'Bee', emoji: '🐝', color: '#FFD700', category: 'animals' },
  { id: 'ladybug', name: 'Ladybug', emoji: '🐞', color: '#FF0000', category: 'animals' },
  { id: 'ant', name: 'Ant', emoji: '🐜', color: '#000000', category: 'animals' },
  { id: 'spider', name: 'Spider', emoji: '🕷️', color: '#000000', category: 'animals' },
  { id: 'scorpion', name: 'Scorpion', emoji: '🦂', color: '#8B4513', category: 'animals' },
  { id: 'dragon', name: 'Dragon', emoji: '🐲', color: '#32CD32', category: 'animals' },
  { id: 'dinosaur', name: 'Dinosaur', emoji: '🦕', color: '#32CD32', category: 'animals' },
  { id: 't-rex', name: 'T-Rex', emoji: '🦖', color: '#32CD32', category: 'animals' },

  // Face Emojis - Popular and expressive faces
  { id: 'grinning-face', name: 'Grinning Face', emoji: '😀', color: '#FFD700', category: 'faces' },
  { id: 'beaming-face', name: 'Beaming Face', emoji: '😁', color: '#FFD700', category: 'faces' },
  { id: 'laughing-face', name: 'Laughing Face', emoji: '😂', color: '#FFD700', category: 'faces' },
  { id: 'rolling-laughing', name: 'Rolling Laughing', emoji: '🤣', color: '#FFD700', category: 'faces' },
  { id: 'winking-face', name: 'Winking Face', emoji: '😉', color: '#FFD700', category: 'faces' },
  { id: 'smiling-face', name: 'Smiling Face', emoji: '😊', color: '#FFD700', category: 'faces' },
  { id: 'smiling-face-hearts', name: 'Smiling Face Hearts', emoji: '🥰', color: '#FFB6C1', category: 'faces' },
  { id: 'kissing-face', name: 'Kissing Face', emoji: '😘', color: '#FFB6C1', category: 'faces' },
  { id: 'heart-eyes', name: 'Heart Eyes', emoji: '😍', color: '#FF69B4', category: 'faces' },
  { id: 'star-struck', name: 'Star Struck', emoji: '🤩', color: '#FFD700', category: 'faces' },
  { id: 'thinking-face', name: 'Thinking Face', emoji: '🤔', color: '#87CEEB', category: 'faces' },
  { id: 'face-with-raised-eyebrow', name: 'Raised Eyebrow', emoji: '🤨', color: '#FFD700', category: 'faces' },
  { id: 'neutral-face', name: 'Neutral Face', emoji: '😐', color: '#D3D3D3', category: 'faces' },
  { id: 'expressionless-face', name: 'Expressionless Face', emoji: '😑', color: '#D3D3D3', category: 'faces' },
  { id: 'face-without-mouth', name: 'Face Without Mouth', emoji: '😶', color: '#D3D3D3', category: 'faces' },
  { id: 'smirking-face', name: 'Smirking Face', emoji: '😏', color: '#FFD700', category: 'faces' },
  { id: 'unamused-face', name: 'Unamused Face', emoji: '😒', color: '#D3D3D3', category: 'faces' },
  { id: 'face-with-rolling-eyes', name: 'Rolling Eyes', emoji: '🙄', color: '#D3D3D3', category: 'faces' },
  { id: 'grimacing-face', name: 'Grimacing Face', emoji: '😬', color: '#FFD700', category: 'faces' },
  { id: 'lying-face', name: 'Lying Face', emoji: '🤥', color: '#FFD700', category: 'faces' },
  { id: 'relieved-face', name: 'Relieved Face', emoji: '😌', color: '#98FB98', category: 'faces' },
  { id: 'pensive-face', name: 'Pensive Face', emoji: '😔', color: '#87CEEB', category: 'faces' },
  { id: 'sleepy-face', name: 'Sleepy Face', emoji: '😪', color: '#87CEEB', category: 'faces' },
  { id: 'drooling-face', name: 'Drooling Face', emoji: '🤤', color: '#FFD700', category: 'faces' },
  { id: 'sleeping-face', name: 'Sleeping Face', emoji: '😴', color: '#87CEEB', category: 'faces' },
  { id: 'face-with-medical-mask', name: 'Medical Mask', emoji: '😷', color: '#FFFFFF', category: 'faces' },
  { id: 'face-with-thermometer', name: 'Thermometer Face', emoji: '🤒', color: '#FFD700', category: 'faces' },
  { id: 'face-with-head-bandage', name: 'Head Bandage', emoji: '🤕', color: '#FFFFFF', category: 'faces' },
  { id: 'nauseated-face', name: 'Nauseated Face', emoji: '🤢', color: '#32CD32', category: 'faces' },
  { id: 'face-vomiting', name: 'Vomiting Face', emoji: '🤮', color: '#32CD32', category: 'faces' },
  { id: 'sneezing-face', name: 'Sneezing Face', emoji: '🤧', color: '#FFD700', category: 'faces' },
  { id: 'hot-face', name: 'Hot Face', emoji: '🥵', color: '#FF4500', category: 'faces' },
  { id: 'cold-face', name: 'Cold Face', emoji: '🥶', color: '#87CEEB', category: 'faces' },
  { id: 'woozy-face', name: 'Woozy Face', emoji: '🥴', color: '#FFD700', category: 'faces' },
  { id: 'dizzy-face', name: 'Dizzy Face', emoji: '😵', color: '#FFD700', category: 'faces' },
  { id: 'exploding-head', name: 'Exploding Head', emoji: '🤯', color: '#FFD700', category: 'faces' },
  { id: 'cowboy-hat-face', name: 'Cowboy Hat', emoji: '🤠', color: '#8B4513', category: 'faces' },
  { id: 'partying-face', name: 'Partying Face', emoji: '🥳', color: '#FFD700', category: 'faces' },
  { id: 'disguised-face', name: 'Disguised Face', emoji: '🥸', color: '#8B4513', category: 'faces' },
  { id: 'sunglasses-face', name: 'Sunglasses Face', emoji: '😎', color: '#000000', category: 'faces' },
  { id: 'nerd-face', name: 'Nerd Face', emoji: '🤓', color: '#87CEEB', category: 'faces' },
  { id: 'face-with-monocle', name: 'Monocle Face', emoji: '🧐', color: '#8B4513', category: 'faces' },
  { id: 'confused-face', name: 'Confused Face', emoji: '😕', color: '#FFD700', category: 'faces' },
  { id: 'worried-face', name: 'Worried Face', emoji: '😟', color: '#FFD700', category: 'faces' },
  { id: 'slightly-frowning-face', name: 'Slightly Frowning', emoji: '🙁', color: '#D3D3D3', category: 'faces' },
  { id: 'frowning-face', name: 'Frowning Face', emoji: '☹️', color: '#D3D3D3', category: 'faces' },
  { id: 'open-mouth-face', name: 'Open Mouth', emoji: '😮', color: '#FFD700', category: 'faces' },
  { id: 'hushed-face', name: 'Hushed Face', emoji: '😯', color: '#FFD700', category: 'faces' },
  { id: 'astonished-face', name: 'Astonished Face', emoji: '😲', color: '#FFD700', category: 'faces' },
  { id: 'flushed-face', name: 'Flushed Face', emoji: '😳', color: '#FF69B4', category: 'faces' },
  { id: 'pleading-face', name: 'Pleading Face', emoji: '🥺', color: '#FFB6C1', category: 'faces' },
  { id: 'frowning-open-mouth', name: 'Frowning Open Mouth', emoji: '😦', color: '#D3D3D3', category: 'faces' },
  { id: 'anguished-face', name: 'Anguished Face', emoji: '😧', color: '#FFD700', category: 'faces' },
  { id: 'fearful-face', name: 'Fearful Face', emoji: '😨', color: '#87CEEB', category: 'faces' },
  { id: 'anxious-face', name: 'Anxious Face', emoji: '😰', color: '#87CEEB', category: 'faces' },
  { id: 'sad-but-relieved-face', name: 'Sad but Relieved', emoji: '😥', color: '#D3D3D3', category: 'faces' },
  { id: 'crying-face', name: 'Crying Face', emoji: '😢', color: '#87CEEB', category: 'faces' },
  { id: 'loudly-crying-face', name: 'Loudly Crying', emoji: '😭', color: '#87CEEB', category: 'faces' },
  { id: 'screaming-face', name: 'Screaming Face', emoji: '😱', color: '#FFD700', category: 'faces' },
  { id: 'confounded-face', name: 'Confounded Face', emoji: '😖', color: '#FFD700', category: 'faces' },
  { id: 'persevering-face', name: 'Persevering Face', emoji: '😣', color: '#FFD700', category: 'faces' },
  { id: 'disappointed-face', name: 'Disappointed Face', emoji: '😞', color: '#D3D3D3', category: 'faces' },
  { id: 'downcast-face', name: 'Downcast Face', emoji: '😓', color: '#87CEEB', category: 'faces' },
  { id: 'tired-face', name: 'Tired Face', emoji: '😫', color: '#FFD700', category: 'faces' },
  { id: 'yawning-face', name: 'Yawning Face', emoji: '🥱', color: '#FFD700', category: 'faces' },
  { id: 'steam-from-nose', name: 'Steam from Nose', emoji: '😤', color: '#FFD700', category: 'faces' },
  { id: 'pouting-face', name: 'Pouting Face', emoji: '😡', color: '#FF4500', category: 'faces' },
  { id: 'angry-face', name: 'Angry Face', emoji: '😠', color: '#FF4500', category: 'faces' },
  { id: 'face-with-symbols', name: 'Face with Symbols', emoji: '🤬', color: '#FF4500', category: 'faces' },
  { id: 'smiling-face-horns', name: 'Smiling Face Horns', emoji: '😈', color: '#000000', category: 'faces' },
  { id: 'angry-face-horns', name: 'Angry Face Horns', emoji: '👿', color: '#000000', category: 'faces' },
  { id: 'skull', name: 'Skull', emoji: '💀', color: '#808080', category: 'faces' },
  { id: 'skull-crossbones', name: 'Skull Crossbones', emoji: '☠️', color: '#808080', category: 'faces' },
  { id: 'pile-of-poo', name: 'Pile of Poo', emoji: '💩', color: '#8B4513', category: 'faces' },
  { id: 'clown-face', name: 'Clown Face', emoji: '🤡', color: '#FFD700', category: 'faces' },
  { id: 'ogre', name: 'Ogre', emoji: '👹', color: '#FF4500', category: 'faces' },
  { id: 'goblin', name: 'Goblin', emoji: '👺', color: '#FF4500', category: 'faces' },
  { id: 'ghost', name: 'Ghost', emoji: '👻', color: '#FFFFFF', category: 'faces' },
  { id: 'alien', name: 'Alien', emoji: '👽', color: '#32CD32', category: 'faces' },
  { id: 'alien-monster', name: 'Alien Monster', emoji: '👾', color: '#32CD32', category: 'faces' },
  { id: 'robot', name: 'Robot', emoji: '🤖', color: '#C0C0C0', category: 'faces' },

  // Objects
  { id: 'star', name: 'Star', emoji: '⭐', color: '#FFD700', category: 'objects' },
  { id: 'rainbow', name: 'Rainbow', emoji: '🌈', color: '#FF69B4', category: 'objects' },
  { id: 'sun', name: 'Sun', emoji: '☀️', color: '#FFD700', category: 'objects' },
  { id: 'moon', name: 'Moon', emoji: '🌙', color: '#C0C0C0', category: 'objects' },
  { id: 'balloon', name: 'Balloon', emoji: '🎈', color: '#FF69B4', category: 'objects' },
  { id: 'gift', name: 'Gift', emoji: '🎁', color: '#FF0000', category: 'objects' },
  { id: 'trophy', name: 'Trophy', emoji: '🏆', color: '#FFD700', category: 'objects' },
  { id: 'medal', name: 'Medal', emoji: '🏅', color: '#FFD700', category: 'objects' },
  { id: 'crown', name: 'Crown', emoji: '👑', color: '#FFD700', category: 'objects' },
  { id: 'diamond', name: 'Diamond', emoji: '💎', color: '#00BFFF', category: 'objects' },

  // Nature
  { id: 'tree', name: 'Tree', emoji: '🌳', color: '#228B22', category: 'nature' },
  { id: 'flower', name: 'Flower', emoji: '🌸', color: '#FFB6C1', category: 'nature' },
  { id: 'butterfly', name: 'Butterfly', emoji: '🦋', color: '#FF69B4', category: 'nature' },
  { id: 'bird', name: 'Bird', emoji: '🐦', color: '#87CEEB', category: 'nature' },
  { id: 'fish', name: 'Fish', emoji: '🐠', color: '#FF6347', category: 'nature' },
  { id: 'leaf', name: 'Leaf', emoji: '🍃', color: '#32CD32', category: 'nature' },
  { id: 'mountain', name: 'Mountain', emoji: '⛰️', color: '#8B4513', category: 'nature' },
  { id: 'ocean', name: 'Ocean', emoji: '🌊', color: '#0000FF', category: 'nature' },
  { id: 'cloud', name: 'Cloud', emoji: '☁️', color: '#FFFFFF', category: 'nature' },
  { id: 'fire', name: 'Fire', emoji: '🔥', color: '#FF4500', category: 'nature' },

  // Symbols
  { id: 'heart', name: 'Heart', emoji: '❤️', color: '#FF0000', category: 'symbols' },
  { id: 'peace', name: 'Peace', emoji: '☮️', color: '#32CD32', category: 'symbols' },
  { id: 'music', name: 'Music', emoji: '🎵', color: '#FF69B4', category: 'symbols' },
  { id: 'art', name: 'Art', emoji: '🎨', color: '#FF6347', category: 'symbols' },
  { id: 'book', name: 'Book', emoji: '📚', color: '#8B4513', category: 'symbols' },
  { id: 'lightbulb', name: 'Lightbulb', emoji: '💡', color: '#FFD700', category: 'symbols' },
  { id: 'rocket', name: 'Rocket', emoji: '🚀', color: '#FF4500', category: 'symbols' },
  { id: 'magic', name: 'Magic', emoji: '✨', color: '#FFD700', category: 'symbols' },
  { id: 'sparkles', name: 'Sparkles', emoji: '🌟', color: '#FFD700', category: 'symbols' },
  { id: 'rainbow-flag', name: 'Rainbow Flag', emoji: '🏳️‍🌈', color: '#FF69B4', category: 'symbols' }
];

/**
 * Get avatar data by ID
 * @param avatarId - The avatar ID
 * @returns Avatar data or null if not found
 */
export const getAvatarById = (avatarId: string): AvatarData | null => {
  return AVATAR_OPTIONS.find(avatar => avatar.id === avatarId) || null;
};

/**
 * Get all avatars by category
 * @param category - The category to filter by
 * @returns Array of avatars in the category
 */
export const getAvatarsByCategory = (category: AvatarData['category']): AvatarData[] => {
  return AVATAR_OPTIONS.filter(avatar => avatar.category === category);
};

/**
 * Get all available categories
 * @returns Array of category objects
 */
export const getAvatarCategories = () => [
  { id: 'animals', name: 'Animals', icon: '🐱' },
  { id: 'faces', name: 'Faces', icon: '😀' },
  { id: 'objects', name: 'Objects', icon: '⭐' },
  { id: 'nature', name: 'Nature', icon: '🌳' },
  { id: 'symbols', name: 'Symbols', icon: '❤️' }
] as const;

/**
 * Get default avatar (first animal)
 * @returns Default avatar data
 */
export const getDefaultAvatar = (): AvatarData => {
  return AVATAR_OPTIONS.find(avatar => avatar.category === 'animals') || AVATAR_OPTIONS[0];
};

/**
 * Validate if an avatar ID exists
 * @param avatarId - The avatar ID to validate
 * @returns True if avatar exists
 */
export const isValidAvatarId = (avatarId: string): boolean => {
  return AVATAR_OPTIONS.some(avatar => avatar.id === avatarId);
};
